import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useMediaQuery } from "react-responsive";
import { GoVideo } from "react-icons/go";
import loader from "../assets/img/loader/loader1.jpg";

import { getProjects } from "../features/project/projectSlice";

export default function CaseStudyRecentWork({ activeTab, activeFilter }) {
  const dispatch = useDispatch();
  const servicesRef = useRef(null);
  const isDesktop = useMediaQuery({ minWidth: 768 });

  const { projects } = useSelector((state) => state.project);
  const [videoLoaded, setVideoLoaded] = useState({});

  const videoProps = isDesktop
    ? {
        onMouseEnter: (e) => {
          const video = e.currentTarget;
          if (video.paused) video.play().catch(() => {});
        },
        onMouseLeave: (e) => {
          const video = e.currentTarget;
          if (!video.paused) {
            video.pause();
            video.currentTime = 0;
          }
        },
      }
    : {
        autoPlay: true,
        muted: true,
        playsInline: true,
      };

  useEffect(() => {
    dispatch(getProjects());
  }, [dispatch]);

  useEffect(() => {
    const revealElements = servicesRef.current.querySelectorAll("[data-reveal]");
    const scrollReveal = () => {
      revealElements.forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight / 1.15) {
          el.classList.add("revealed");
        } else {
          el.classList.remove("revealed");
        }
      });
    };

    window.addEventListener("scroll", scrollReveal);
    scrollReveal();
    return () => window.removeEventListener("scroll", scrollReveal);
  }, [activeFilter, activeTab, projects]);

  const handleVideoLoaded = (key) => {
    setVideoLoaded((prev) => ({ ...prev, [key]: true }));
  };

  // Filter projects based on active tab
  const filteredProjects = projects?.filter((project) => {
    if (activeTab === "Type of Project") {
      return (
        activeFilter === "All" ||
        project.categories?.includes(activeFilter)
      );
    } else if (activeTab === "Type of Industry") {
      return activeFilter === "All" || project.industry === activeFilter;
    }
    return true;
  });

  return (
    <div className="case-study-wrap">
      <div className="case-study-inner" ref={servicesRef}>
        {filteredProjects?.map((item, index) => {
          const isVideo = item.type === "video";
          const mediaUrl = isVideo ? item.video?.url : item.images?.[0]?.url;

          return (
            <div className="card" data-reveal key={index}>
              <div className={`media ${item.mediaClass || ""}`}>
                {isVideo ? (
                  <>
                    {!videoLoaded[item.title] && (
                      <img
                        src={loader}
                        alt="loading"
                        className="video-placeholder"
                      />
                    )}
                    <video
                      key={item.title}
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedData={() => handleVideoLoaded(item.title)}
                      {...videoProps}
                      style={{ opacity: videoLoaded[item.title] ? 1 : 0 }}
                    >
                      <source src={mediaUrl} type="video/mp4" />
                    </video>
                    <div className="roarz__watermark">
                      <GoVideo />
                    </div>
                  </>
                ) : (
                  <LazyLoadImage
                    key={item.title}
                    src={mediaUrl}
                    alt={item.title}
                    effect="blur"
                    placeholderSrc={loader}
                    width="100%"
                    height="100%"
                  />
                )}
              </div>

              <div className="meta">
                <h3 data-reveal="left">
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                </h3>
                <div className="tags" data-reveal>
                  {item.categories?.map((tag, idx) => (
                    <span key={idx}>{tag.toUpperCase()}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
